/**
 * `AssetBinding` union'ının İKİ dalı için tek alan:
 *
 *   { kind: "tracked"; instrumentId }              → merkezden izlenen araç
 *   { kind: "manual"; label; currency }            → yalnızca elle değerlenen
 *
 * AŞAMA 2'DE DEĞİŞEN: izlenen araç artık SERBEST METİN değil, merkezin
 * `/resolve` uç noktasından seçilen bir aday. Seçimden sonra araç `/track` ile
 * izlemeye alınır ve künyesi cihaz önbelleğine yazılır (İzleme ekranı onu
 * oradan okur).
 *
 * ELLE KİMLİK ALANI NİÇİN HÂLÂ VAR: merkez erişilemezken de hareket
 * girilebilmesi ZORUNLU (uçak modu kuralı). Arama tek yol olsaydı, merkez
 * kapalıyken kullanıcı defterine hiçbir izlenen araç yazamazdı. O yüzden alan
 * duruyor ama İKİNCİL: merkez varken kapalı bir katlanır bölümde, merkez
 * yokken doğrudan görünür.
 */

import { useId, useState } from "react";
import type { AssetBinding } from "@parafon/contracts";
import type { ResolveCandidateDto, TrackStatusDto } from "@parafon/contracts/central-api";
import { M } from "../dil";
import { useCentral } from "../central/CentralContext";
import { currencyLabel, isCurrencyUnknown } from "../lib/format";
import { describeTrackState } from "../lib/trackState";
import { errorText } from "../lib/rpcError";
import { Button, Field, Notice, Select, TextInput } from "../ui/primitives";
import { InstrumentSearch } from "./InstrumentSearch";
import { CurrencySelect } from "../ui/CurrencySelect";

export interface AssetBindingFieldProps {
  readonly value: AssetBinding;
  readonly onChange: (next: AssetBinding) => void;
  readonly disabled?: boolean;
  readonly errors?: {
    readonly instrumentId?: string;
    readonly label?: string;
    readonly currency?: string;
  };
  /** Nakit hareketlerinde alan kapalı başlar; kullanıcı isterse açar. */
  readonly note?: string;
  /** Testler gecikmeyi kısaltır. */
  readonly searchDebounceMs?: number;
}

/** Seçilen adayın ekranda gösterilecek künyesi — kimlikten fazlası. */
interface SelectedInfo {
  readonly instrumentId: string;
  readonly symbol: string;
  readonly name: string;
  readonly exchange: string;
  readonly currency: string;
}

export function AssetBindingField({
  value,
  onChange,
  disabled = false,
  errors,
  note,
  searchDebounceMs,
}: AssetBindingFieldProps) {
  const kindId = useId();
  const searchId = useId();
  const manualIdId = useId();
  const labelId = useId();
  const currencyId = useId();

  const { central, prices } = useCentral();

  const [selected, setSelected] = useState<SelectedInfo | null>(null);
  const [trackStatus, setTrackStatus] = useState<TrackStatusDto | null>(null);
  const [trackError, setTrackError] = useState<unknown>(null);
  const [tracking, setTracking] = useState(false);

  const setKind = (kind: string): void => {
    if (kind === "tracked") {
      onChange({
        kind: "tracked",
        instrumentId: value.kind === "manual" ? value.label : value.instrumentId,
      });
    } else {
      onChange({
        kind: "manual",
        label: value.kind === "tracked" ? value.instrumentId : value.label,
        currency: value.kind === "manual" ? value.currency : "TRY",
      });
    }
  };

  /** "Bulamadım" ve `unresolved` yollarının ikisi de buraya çıkar. */
  const toManual = (label: string, currency: string): void => {
    onChange({ kind: "manual", label, currency });
    setSelected(null);
    setTrackStatus(null);
    setTrackError(null);
  };

  const onSelectCandidate = (candidate: ResolveCandidateDto): void => {
    onChange({ kind: "tracked", instrumentId: candidate.instrumentId });
    setSelected({
      instrumentId: candidate.instrumentId,
      symbol: candidate.symbol,
      name: candidate.name,
      exchange: candidate.exchange,
      currency: candidate.currency,
    });
    setTrackStatus(null);
    setTrackError(null);
    if (central === null) return;

    setTracking(true);
    void central
      .track(candidate.instrumentId)
      .then(async (status) => {
        setTrackStatus(status);
        // Künye ve durum cihaz önbelleğine yazılır ki İzleme ekranı aracı
        // eşitleme beklemeden gösterebilsin. `currency: "?"` NULL olarak
        // yazılır — bilinmeyeni bilinir gibi saklamak sessiz hata olurdu.
        if (prices !== null) {
          await prices.putInstrument({
            instrumentId: candidate.instrumentId,
            symbol: candidate.symbol,
            exchange: candidate.exchange,
            assetClass: candidate.assetClass,
            currency: isCurrencyUnknown(candidate.currency) ? null : candidate.currency,
            name: candidate.name,
            centralState: status.state,
            backfilledTo: status.backfilledTo,
            horizonFrom: status.horizonFrom,
            pendingJobs: status.pendingJobs,
            etaSeconds: status.etaSeconds,
            ...(status.lastError === undefined ? {} : { lastError: status.lastError }),
          });
        }
      })
      .catch((error: unknown) => setTrackError(error))
      .finally(() => setTracking(false));
  };

  const trackedId = value.kind === "tracked" ? value.instrumentId : "";
  const showSearch =
    value.kind === "tracked" && (selected === null || selected.instrumentId !== trackedId);
  /**
   * Hata metni TEK yerde durur: hangi girdi o an KULLANILABİLİR ise onun
   * altında. İki yere basmak, aynı cümleyi iki kez okumak (ve testte iki
   * eşleşme) demekti.
   */
  const errorOnManualId = central === null;
  const searchError =
    !errorOnManualId && errors?.instrumentId !== undefined ? errors.instrumentId : undefined;
  const manualIdError =
    errorOnManualId && errors?.instrumentId !== undefined ? errors.instrumentId : undefined;

  return (
    <fieldset className="rounded-pf border border-line bg-surface-2 p-3" disabled={disabled}>
      <legend className="px-1 text-[12px] font-semibold text-muted">{M.izleme.baglama.varlik}</legend>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={M.izleme.baglama.tur} htmlFor={kindId}>
          <Select
            id={kindId}
            value={value.kind}
            onChange={(e) => setKind(e.currentTarget.value)}
            options={[
              { value: "tracked", label: M.izleme.baglama.turIzlenen },
              { value: "manual", label: M.izleme.baglama.turElle },
            ]}
          />
        </Field>

        {value.kind === "tracked" ? (
          <div className="flex flex-col gap-2 sm:col-span-2">
            {central === null ? (
              // Merkezsiz dal: bileşen kendi sebep kutusunu çizer. `Field`
              // sarmalayıcısı KULLANILMAZ — ortada bağlanacak bir girdi yok,
              // boşa düşen bir <label for> erişilebilirliği bozardı.
              <InstrumentSearch
                inputId={searchId}
                onSelect={onSelectCandidate}
                onNotFound={() => toManual(trackedId, "TRY")}
                disabled={disabled}
              />
            ) : showSearch ? (
              <Field
                label={M.izleme.baglama.aracAra}
                htmlFor={searchId}
                hint={M.izleme.baglama.aracAraIpucu}
                {...(searchError === undefined ? {} : { error: searchError })}
              >
                <InstrumentSearch
                  inputId={searchId}
                  onSelect={onSelectCandidate}
                  onNotFound={() => toManual(trackedId, "TRY")}
                  disabled={disabled}
                  {...(searchDebounceMs === undefined ? {} : { debounceMs: searchDebounceMs })}
                />
              </Field>
            ) : (
              selected !== null && (
                <div className="rounded-pf border border-line bg-surface p-2 text-[13px]">
                  <p className="flex flex-wrap items-baseline gap-x-2">
                    <span className="pf-num font-semibold text-ink">{selected.symbol}</span>
                    <span className="text-ink">{selected.name}</span>
                    <span className="pf-tag">{selected.exchange}</span>
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted">
                    <span className="pf-num">{selected.instrumentId}</span>
                    {M.izleme.baglama.paraBirimiOnEk}
                    {currencyLabel(selected.currency)}
                  </p>
                  <div className="mt-1.5">
                    <Button
                      onClick={() => {
                        setSelected(null);
                        setTrackStatus(null);
                        setTrackError(null);
                      }}
                    >
                      {M.izleme.baglama.baskaArac}
                    </Button>
                  </div>
                </div>
              )
            )}

            {tracking && <p className="text-[12px] text-muted">{M.izleme.baglama.izlemeyeAliniyor}</p>}

            {trackStatus !== null &&
              (trackStatus.state === "unresolved" ? (
                <Notice tone="danger" title={M.izleme.baglama.cozulemediBaslik}>
                  <p>{describeTrackState("unresolved").meaning}</p>
                  <div className="mt-1">
                    <Button
                      variant="primary"
                      onClick={() =>
                        toManual(
                          selected?.name ?? trackedId,
                          // Merkez para birimini bilmiyorsa BOŞ bırakılır:
                          // uydurmak yerine kullanıcıya sorulur (form zorunlu
                          // kılıyor).
                          selected !== null && !isCurrencyUnknown(selected.currency)
                            ? selected.currency
                            : "",
                        )
                      }
                    >
                      {M.izleme.baglama.elleyeCevir}
                    </Button>
                  </div>
                </Notice>
              ) : (
                <Notice tone="info" title={describeTrackState(trackStatus.state).title}>
                  <p>{describeTrackState(trackStatus.state).meaning}</p>
                  <p className="text-[12px] text-muted">
                    {M.izleme.baglama.ilerlemeOnce}
                    <strong>{M.izleme.baglama.ilerlemeEkran}</strong>
                    {M.izleme.baglama.ilerlemeSonra}
                  </p>
                </Notice>
              ))}

            {trackError !== null && (
              <Notice tone="warn" title={M.izleme.baglama.alinamadiBaslik}>
                <p>{errorText(trackError)}</p>
                <p>
                  {M.izleme.baglama.alinamadiMetin}
                </p>
              </Notice>
            )}

            {/*
              ELLE KİMLİK: merkez varken katlanmış, merkez yokken açık gelir.
              Uçak modunda tek çıkış kapısı bu alan.
            */}
            <details open={central === null}>
              <summary className="cursor-pointer text-[12px] text-muted">
                {M.izleme.baglama.merkezsizDevam}
              </summary>
              <div className="mt-2">
                <Field
                  label={M.izleme.baglama.aracKimligi}
                  htmlFor={manualIdId}
                  hint={M.izleme.baglama.aracKimligiIpucu}
                  {...(manualIdError === undefined ? {} : { error: manualIdError })}
                >
                  <TextInput
                    id={manualIdId}
                    className="pf-num"
                    value={trackedId}
                    placeholder="BIST:THYAO"
                    aria-invalid={errors?.instrumentId !== undefined}
                    onChange={(e) => {
                      setSelected(null);
                      onChange({ kind: "tracked", instrumentId: e.currentTarget.value });
                    }}
                  />
                </Field>
              </div>
            </details>
          </div>
        ) : (
          <>
            <Field
              label={M.izleme.baglama.varlikEtiketi}
              htmlFor={labelId}
              required
              hint={M.izleme.baglama.etiketIpucu}
              {...(errors?.label === undefined ? {} : { error: errors.label })}
            >
              <TextInput
                id={labelId}
                value={value.label}
                placeholder={M.izleme.baglama.etiketOrnek}
                aria-invalid={errors?.label !== undefined}
                onChange={(e) =>
                  onChange({ kind: "manual", label: e.currentTarget.value, currency: value.currency })
                }
              />
            </Field>
            <Field
              label={M.izleme.baglama.varlikParaBirimi}
              htmlFor={currencyId}
              required
              {...(errors?.currency === undefined
                ? value.currency === ""
                  ? { hint: M.izleme.baglama.paraBirimiBilinmiyor }
                  : {}
                : { error: errors.currency })}
            >
              <CurrencySelect
                id={currencyId}
                className="pf-fig"
                value={value.currency}
                invalid={errors?.currency !== undefined}
                onChange={(code) =>
                  onChange({
                    kind: "manual",
                    label: value.label,
                    currency: code,
                  })
                }
              />
            </Field>
          </>
        )}
      </div>

      {note !== undefined && <p className="mt-2 text-[12px] text-faint">{note}</p>}
    </fieldset>
  );
}
